// src/pages/Login.tsx

import React, { useState, type JSX } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import logoLight from "../assets/images/logo-light.png";
import withRouter from "../Components/Common/withRouter";
import ParticlesAuth from "../Components/Common/ParticlesAuth/ParticlesAuth";
import { toast } from "react-toastify";
import Loader from "../Components/Common/Loader";
import { jwtDecode } from "jwt-decode";
import userServiceObj from "../ApiService/userService"; // Updated import path
import { LOCAL_STORAGE } from "Constant/constant";
import Storage from "Helper/localStorageHelper";

const Login = (props: any): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonClicked, setButtonClicked] = useState<boolean>(false);
  const navigate = useNavigate();

  const validation = useFormik({
    enableReinitialize: false,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter valid email")
        .required("Please enter email"),
      password: Yup.string().required("Please enter password"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setButtonClicked(true);

        // Use userService for login API call
        const response = await userServiceObj.login({
          email: values.email,
          password: values.password,
        });

        setLoading(false);
        setButtonClicked(false);

        if (response.status === 200) {
          const { data } = response.data;

          if (data?.accessToken) {
            const decodedData = jwtDecode(data?.accessToken);

            if (decodedData?.exp) {
              const expirationDate = new Date(decodedData.exp * 1000); // Convert seconds to milliseconds

              Storage.setData(
                LOCAL_STORAGE.TOKEN_EXPIRE,
                expirationDate.toISOString()
              );
              Storage.setData(LOCAL_STORAGE.REFRESH_TOKEN, data?.refresh_token);
              Storage.setData(LOCAL_STORAGE.TOKEN, data?.accessToken);
              navigate("/dashboard");
            }
          }
        } else {
          toast.error(response.data.message);
        }
      } catch (err: any) {
        console.error("Login error:", err);
        setLoading(false);
        setButtonClicked(false);

        if (err?.statusCode === 401 || err?.code === 401) {
          toast.error(err?.message || "Invalid credentials");
        } else if (err?.message) {
          toast.error(err.message);
        } else {
          toast.error("An error occurred during login");
        }
      }
    },
  });

  document.title = "Login | Go-Finance-Portal";

  return (
    <React.Fragment>
      {loading && <Loader />}
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={logoLight} alt="" height="20" />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">Go-Finance-Portal</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Welcome Back!</h5>
                      <p className="text-muted">
                        Sign in to continue to Go-Finance-Portal.
                      </p>
                    </div>
                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                        action="#"
                      >
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            Email*
                          </Label>
                          <Input
                            name="email"
                            className="form-control"
                            placeholder="Enter email"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email &&
                              validation.errors.email
                                ? true
                                : false
                            }
                          />
                          {validation.touched.email &&
                          validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="password" className="form-label">
                            Password*
                          </Label>
                          <Input
                            name="password"
                            className="form-control"
                            placeholder="Enter password"
                            type="password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password || ""}
                            invalid={
                              validation.touched.password &&
                              validation.errors.password
                                ? true
                                : false
                            }
                          />
                          {validation.touched.password &&
                          validation.errors.password ? (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mt-4">
                          <Button
                            color="success"
                            disabled={loading || buttonClicked}
                            className="btn btn-success w-100"
                            type="submit"
                          >
                            {loading ? (
                              <Spinner size="sm" className="me-2">
                                {" "}
                                Loading...{" "}
                              </Spinner>
                            ) : null}
                            Sign In
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(Login);
